import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import mahiLogo from "../../assets/images/mahi-download-logo.png";

interface MissionAdminLayoutProps {
  children: React.ReactNode;
}

const MissionAdminLayout: React.FC<MissionAdminLayoutProps> = ({
  children,
}) => {
  const location = useLocation();
  const [isReviewsMenuOpen, setIsReviewsMenuOpen] = useState(true);
  const [isBrandContractMenuOpen, setIsBrandContractMenuOpen] = useState(true);

  useEffect(() => {
    // mission admin 페이지 진입 시 root 요소의 max-width 제한 해제
    const rootElement = document.getElementById("root");
    if (rootElement) {
      rootElement.style.maxWidth = "none";
      rootElement.style.boxShadow = "none";
    }

    // cleanup 함수: 컴포넌트 언마운트 시 원래 스타일로 복원
    return () => {
      if (rootElement) {
        rootElement.style.maxWidth = "742px";
        rootElement.style.boxShadow = "0 0 10px rgba(0, 0, 0, 0.1)";
      }
    };
  }, []);

  const menuItems = [
    { path: "/ops/mission/campaigns", label: "캠페인 목록" },
    { path: "/ops/mission/campaigns/create", label: "캠페인 생성" },
    { path: "/ops/mission/templates", label: "미션 템플릿 관리" },
  ];

  const brandContractMenuItems = [
    { path: "/ops/mission/brands", label: "브랜드 관리" },
    { path: "/ops/mission/contracts/create", label: "계약 생성" },
    { path: "/ops/mission/contracts/connect", label: "캠페인 연결" },
    { path: "/ops/mission/settlement-issue", label: "정산서 발급" },
  ];

  const reviewSubMenuItems = [
    { path: "/ops/mission/reviews/submissions", label: "미션 제출검수" },
    {
      path: "/ops/mission/reviews/successes",
      label: "미션 성공처리(페이백 진행)",
    },
    {
      path: "/ops/mission/reviews/failures",
      label: "미션 실패처리(예치금 몰수)",
    },
    { path: "/ops/mission/reviews/withdrawals", label: "출금요청 검수" },
  ];

  const isActive = (path: string) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  const isReviewPathActive = () => {
    return (
      location.pathname === "/ops/mission/reviews" ||
      location.pathname.startsWith("/ops/mission/reviews/")
    );
  };

  const isBrandContractPathActive = () => {
    return (
      location.pathname === "/ops/mission/brands" ||
      location.pathname.startsWith("/ops/mission/contracts/")
    );
  };

  // 검수 관리 하위 메뉴 중 하나가 활성화되어 있으면 메뉴 열기
  useEffect(() => {
    if (isReviewPathActive()) {
      setIsReviewsMenuOpen(true);
    }
    if (isBrandContractPathActive()) {
      setIsBrandContractMenuOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* 사이드바 */}
      <aside className="bg-white border-r border-gray-200 w-64">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <img
              src={mahiLogo}
              alt="마감히어로"
              className="w-10 h-10 object-contain flex-shrink-0"
            />
            <h1 className="font-bold text-lg">
              미션 어드민
            </h1>
          </div>
        </div>

        <nav className="p-4">
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? "bg-blue-50 text-blue-700 font-medium"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <span>{item.label}</span>
                </Link>
              </li>
            ))}

            {/* 검수 관리 토글 메뉴 */}
            <li>
              <div className="flex items-center">
                <Link
                  to="/ops/mission/reviews"
                  className={`flex-1 flex items-center px-4 py-2 rounded-lg transition-colors ${
                    isReviewPathActive()
                      ? "bg-blue-50 text-blue-700 font-medium"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <span>검수 관리</span>
                </Link>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setIsReviewsMenuOpen(!isReviewsMenuOpen);
                  }}
                  className="p-2 hover:bg-gray-100 rounded"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${
                      isReviewsMenuOpen ? "rotate-90" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
              {isReviewsMenuOpen && (
                <ul className="mt-1 ml-4 space-y-1">
                  {reviewSubMenuItems.map((item) => (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        className={`flex items-center px-4 py-2 rounded-lg transition-colors text-sm ${
                          isActive(item.path)
                            ? "bg-blue-50 text-blue-700 font-medium"
                            : "text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        <span className="ml-2">{item.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>

            {/* 브랜드/계약 관리 토글 메뉴 */}
            <li className="pt-2 border-t border-gray-200">
              <div className="flex items-center">
                <div
                  className={`flex-1 flex items-center px-4 py-2 rounded-lg transition-colors ${
                    isBrandContractPathActive()
                      ? "bg-blue-50 text-blue-700 font-medium"
                      : "text-gray-700"
                  }`}
                >
                  <span>브랜드/계약 관리</span>
                </div>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setIsBrandContractMenuOpen(!isBrandContractMenuOpen);
                  }}
                  className="p-2 hover:bg-gray-100 rounded"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${
                      isBrandContractMenuOpen ? "rotate-90" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
              {isBrandContractMenuOpen && (
                <ul className="mt-1 ml-4 space-y-1">
                  {brandContractMenuItems.map((item) => (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        className={`flex items-center px-4 py-2 rounded-lg transition-colors text-sm ${
                          isActive(item.path)
                            ? "bg-blue-50 text-blue-700 font-medium"
                            : "text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        <span className="ml-2">{item.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          </ul>
        </nav>
      </aside>

      {/* 메인 컨텐츠 */}
      <main className="flex-1 overflow-auto">
        <div className="p-8">{children}</div>
      </main>
    </div>
  );
};

export default MissionAdminLayout;
